import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSettings from 'pages/AdminPanel/AdminSettings';
import DeleteAccount from 'pages/AdminPanel/DeleteAccount';
import { UserActions } from 'store/User/Actions';
import { hasValidSubscription, useUser } from 'store/User/Context';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { AdminPanelProvider } from 'utils/context/AdminPanelContex';
import { OrganizationRole } from 'utils/types/BrokerModels';

import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import { IconEnum } from 'components/common/Icon';
import StaticModuleList from 'components/common/StaticModuleList';
import ActivePackagesSettings from 'components/mySettings/ActivePackagesSettings';
import ChangePasswordSettings from 'components/mySettings/ChangePasswordSettings';
import InvoicesSettings from 'components/mySettings/InvoicesSettings';
import MyDataSettings from 'components/mySettings/MyDataSettings/MyDataSettings';
import NotificationsSettings from 'components/mySettings/NotificationsSettings';
import V2HeaderSection from 'components/V2HeaderSection';

const Wrapper = styled.div`
    .active .accordion-card-content {
        row-gap: 10px;
    }
`;

const FiltersContainer = styled.div`
    padding: 70px 0 20px 20px;

    @media (max-width: 1100px) {
        padding-right: 20px;
    }
`;

const Container = styled.div`
    padding: 15px 20px 0;

    @media (max-width: 1100px) {
        padding: 20px 20px 0;
    }
`;

enum MySettingsActiveModule {
    MY_DATA = 'Moje dane',
    NOTIFICATIONS = 'Powiadomienia',
    PACKAGE_INFORMATION = 'Informacje o pakiecie',
    BROKER_PANEL = 'Panel pośrednika',
    INVOICES = 'Zamówienia',
    CHANGE_PASSWORD = 'Zmień hasło',
    DELETE_ACCOUNT = 'Usuń konto'
}

const MySettingsPage = () => {
    const { user, dispatch } = useUser();
    const navigate = useNavigate();
    const myDataRef = useRef(null);
    const notificationsRef = useRef(null);
    const packageInfoRef = useRef(null);
    const brokerPanelRef = useRef(null);
    const invoicesRef = useRef(null);
    const changePasswordRef = useRef(null);
    const deleteAccountRef = useRef(null);

    const [activeModule, setActiveModule] = useState<MySettingsActiveModule>(MySettingsActiveModule.MY_DATA);
    const validSubscription = hasValidSubscription(user.userRole);

    const isAdmin = user.organizationRole === OrganizationRole.ORGANIZATION_ADMIN;
    const canDeleteAccount = user.userRole.includes('ROLE_ACC_DELETE');

    const handleModuleClick = (module: MySettingsActiveModule, ref: React.RefObject<HTMLDivElement>) => {
        setActiveModule(module);
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleDeleteAccount = async () => {
        const success = await userApiRequests.deleteAccount();

        if (success) {
            await UserActions.logout(dispatch);
            navigate('/');
        }
    };

    const modules = [
        {
            title: 'Moje dane',
            icon: IconEnum.BAG_WHITE,
            isActive: activeModule === MySettingsActiveModule.MY_DATA,
            onClick: () => handleModuleClick(MySettingsActiveModule.MY_DATA, myDataRef)
        },
        ...validSubscription
            ? [
                {
                    title: 'Powiadomienia',
                    icon: IconEnum.CALENDAR_WHITE,
                    isActive: activeModule === MySettingsActiveModule.NOTIFICATIONS,
                    onClick: () => handleModuleClick(MySettingsActiveModule.NOTIFICATIONS, notificationsRef)
                }
            ]
            : [],
        {
            title: 'Informacje o pakiecie',
            icon: IconEnum.PEOPLES_2_WHITE,
            isActive: activeModule === MySettingsActiveModule.PACKAGE_INFORMATION,
            onClick: () => handleModuleClick(MySettingsActiveModule.PACKAGE_INFORMATION, packageInfoRef)
        },
        ...isAdmin && validSubscription
            ? [
                {
                    title: 'Panel pośrednika',
                    icon: IconEnum.PEOPLES_3_WHITE,
                    isActive: activeModule === MySettingsActiveModule.BROKER_PANEL,
                    onClick: () => handleModuleClick(MySettingsActiveModule.BROKER_PANEL, brokerPanelRef)
                }
            ]
            : [],
        {
            title: 'Zamówienia',
            icon: IconEnum.PEOPLES_3_WHITE,
            isActive: activeModule === MySettingsActiveModule.INVOICES,
            onClick: () => handleModuleClick(MySettingsActiveModule.INVOICES, invoicesRef)
        },
        {
            title: 'Zmień hasło',
            icon: IconEnum.BLOCKED_WHITE,
            isActive: activeModule === MySettingsActiveModule.CHANGE_PASSWORD,
            onClick: () => handleModuleClick(MySettingsActiveModule.CHANGE_PASSWORD, changePasswordRef)
        },
        ...canDeleteAccount
            ? [
                {
                    title: 'Usuń konto',
                    icon: IconEnum.DELETE_WHITE,
                    isActive: activeModule === MySettingsActiveModule.DELETE_ACCOUNT,
                    onClick: () => {
                        handleModuleClick(MySettingsActiveModule.DELETE_ACCOUNT, deleteAccountRef);
                    }
                }
            ]
            : []
    ];

    return (
        <Wrapper>
            <V2HeaderSection title={'Moje ustawienia'}/>
            <FiltersContentWrapper className={'active'}>
                <FiltersContainer className="dark-mode-icons">
                    <StaticModuleList modules={modules} />
                </FiltersContainer>
                <Container className="dark-mode-icons">
                    <MyDataSettings
                        ref={myDataRef}
                        title="Moje dane"
                    />
                    {validSubscription && <NotificationsSettings
                        ref={notificationsRef}
                        title="Powiadomienia"
                    />}
                    <ActivePackagesSettings
                        ref={packageInfoRef}
                        title="Aktywne pakiety"
                    />
                    {isAdmin &&
                        <AdminPanelProvider>
                            <AdminSettings
                                ref={brokerPanelRef}
                                title="Panel pośrednika"
                            />
                        </AdminPanelProvider>}
                    <InvoicesSettings
                        ref={invoicesRef}
                        title="Zamówienia"
                    />
                    <ChangePasswordSettings
                        ref={changePasswordRef}
                        title="Zmień hasło"
                    />
                    {canDeleteAccount && <DeleteAccount ref={deleteAccountRef} onDeleteAccount={handleDeleteAccount} isDark={user.isDarkMode} />}
                </Container>
            </FiltersContentWrapper>
        </Wrapper>
    );
};

export default MySettingsPage;
